import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type AppRole = 'admin' | 'moderator' | 'user';

interface UserRole {
  role: AppRole;
}

export function useUserRole() {
  const { user } = useAuth();

  const { data: roles, isLoading, error } = useQuery({
    queryKey: ['user-roles', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error fetching user roles:', error);
        return [];
      }
      
      return (data as UserRole[]) || [];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const hasRole = (role: AppRole): boolean => {
    return roles?.some(r => r.role === role) ?? false;
  };

  const isAdmin = hasRole('admin');
  const isModerator = hasRole('moderator') || isAdmin;
  const isReviewer = isAdmin || (roles?.some(r => (r.role as string) === 'reviewer') ?? false);

  return {
    roles,
    isLoading,
    error,
    hasRole,
    isAdmin,
    isModerator,
    isReviewer,
  };
}
